import { Link, NavLink, Outlet, useLocation, useParams } from 'react-router-dom';
import { ArrowLeftRight } from 'lucide-react';
import { useI18n } from '@/i18n/I18nProvider';
import { useSession } from '@/session/SessionProvider';
import { NotFoundScreen } from './NotFoundScreen';
import type { MessageKey } from '@/i18n/translate';
import { cn } from '@/lib/macondo';
import { TestAccountBanner, isTestCustomer } from '@/components/TestAccountBanner';

const SECTIONS: Array<{ to: string; key: MessageKey; end: boolean }> = [
  { to: '', key: 'nav.overview', end: true },
  { to: '/invoices', key: 'nav.invoices', end: false },
  { to: '/payments', key: 'nav.payments', end: false },
  { to: '/statement', key: 'nav.statement', end: false },
];

/** Frame of every `/a/:cid/*` screen: whose account this is, a switcher when there are several, and the section nav. */
export function AccountLayout() {
  const { cid = '' } = useParams();
  const { t } = useI18n();
  const { customers } = useSession();
  const location = useLocation();
  const customer = customers.find((c) => c.id === cid);
  // The API answers 404 for a customer outside the session; the UI never even tries.
  if (!customer) return <NotFoundScreen />;
  const base = `/a/${encodeURIComponent(cid)}`;
  // When every account is a test one the frame (Layout) already shows the banner; here only a test account among real ones.
  const flagHere = isTestCustomer(customer) && !customers.every(isTestCustomer);
  return (
    <div className="flex flex-col gap-500">
      {flagHere ? <TestAccountBanner /> : null}
      <div className="flex flex-wrap items-center justify-between gap-x-400 gap-y-100 print:hidden">
        <p className="text-callout-medium text-typography-secondary min-w-0 break-words" data-testid="account-name">
          {customer.name}
          <span className="text-typography-tertiary font-normal"> {customer.currency}</span>
        </p>
        {customers.length > 1 && (
          <Link to="/accounts" className="focus-ring text-callout text-typography-primary inline-flex min-h-[44px] items-center gap-100 underline">
            <ArrowLeftRight size={16} aria-hidden="true" />
            {t('nav.switchAccount')}
          </Link>
        )}
      </div>
      <nav aria-label={t('nav.sections')} className="print:hidden">
        <ul className="flex flex-wrap gap-200">
          {SECTIONS.map((s) => (
            <li key={s.key}>
              <NavLink
                to={`${base}${s.to}`}
                end={s.end}
                className={({ isActive }) =>
                  cn(
                    'focus-ring text-callout-medium inline-flex min-h-[44px] items-center rounded-button border px-400',
                    isActive
                      ? 'bg-surface-inverse-primary text-typography-inverse-primary border-transparent'
                      : 'bg-surface-primary text-typography-primary border-border-primary hover:bg-surface-hover',
                  )
                }
              >
                {t(s.key)}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      {/* key: a change of account or section remounts the page so focus and state start clean */}
      <Outlet key={location.pathname.split('/').slice(0, 3).join('/')} />
    </div>
  );
}
